import React, { useEffect, useState } from "react";
import { useCopy } from "../../utils/globalContext";
import { useCookies } from "react-cookie";
import { getHideNavigation } from "../../store/hideNavigation/hideNavigation";
import { getIsWeb } from "../../@api/config";
import { AnimatedItem, Button, Caption, styled } from "@kivra/react-components";
import { CrossIcon } from "@kivra/react-components/icons";

export const SmartBanner = () => {
  const copy = useCopy();
  const userAgent = navigator.userAgent;
  const androidRegex = /Android/i;
  const iOsRegex = /iPad|iPhone|iPod/;
  const isAndroid = androidRegex.test(userAgent);
  const isIos = iOsRegex.test(userAgent);
  const isNative = getHideNavigation() || !getIsWeb(); // native app
  const [show, setShow] = useState(isAndroid);
  const COOKIE_NAME = "KIVRA_SMART_BANNER";
  const [animation, setAnimation] = useState<"throw-in" | "fade-out">(
    "throw-in",
  );
  const [cookies, setCookie] = useCookies([COOKIE_NAME]);

  const handleOnOpenApp = () => {
    // ios already managed with the metatag in the
    // index, ios does it nativelly
    if (isAndroid) {
      const packageName = "com.kivra.KivraFi";
      const googlePlayUrl = `market://details?id=${packageName}`;

      const androidAppUrl =
        `intent://login#Intent;scheme=kivra;package=${packageName};S.browser_fallback_url=${googlePlayUrl};end`;
      window.location.href = androidAppUrl;

      // fallback if not installed
      setTimeout(() => {
        window.location.href = googlePlayUrl;
      }, 1000);
    }
  };

  const handleOnClose = () => {
    setShow(false);
    setCookie(COOKIE_NAME, true);
  };

  useEffect(() => {
    if (animation === "fade-out") {
      setTimeout(() => handleOnClose(), 200);
    }
  }, [animation]);

  if (isNative) {
    // remove the metatag for ios:
    const metaTag = document.querySelector('meta[name="apple-itunes-app"]');
    if (metaTag && metaTag.parentNode) {
      metaTag.parentNode.removeChild(metaTag);
    }
    // we don't need it
    return null;
  }

  if (!show || cookies[COOKIE_NAME] || isIos) {
    return null;
  }

  return (
    <WrapperBanner data-test-id="smart-banner">
      <AnimatedItem animation={animation}>
        <Banner>
          <BannerRow>
            <a onClick={() => setAnimation("fade-out")}>
              <CrossIcon _color="#000" size={16} />
            </a>
            <Logo src="/assets/kivra-app-logo.png" />
            <Captions>
              <CustomCaption>
                {copy("auth_smart_banner_company_name")}
              </CustomCaption>
              <CustomCaption>
                {copy("auth_smart_banner_descr")}
              </CustomCaption>
            </Captions>
            <ViewButton
              size="medium"
              variant="primary"
              type="button"
              onClick={handleOnOpenApp}
            >
              {copy("auth_smart_banner_install")}
            </ViewButton>
          </BannerRow>
        </Banner>
      </AnimatedItem>
    </WrapperBanner>
  );
};

const WrapperBanner = styled("div")({
  color: "$surface-success",
  position: "fixed",
  left: "$spacing-12",
  right: "$spacing-12",
  bottom: "$spacing-12",
  zIndex: 1101,
});

const Banner = styled("div")({
  padding: "$spacing-12",
  background: "#f0f0f0",
  borderRadius: "$radius-xsmall",
  display: "flex",
  flexDirection: "column",
  gap: "$spacing-12",
  boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
});

const BannerRow = styled("div")({
  display: "flex",
  gap: "$spacing-12",
  alignItems: "center",
});

const Captions = styled("div")({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "$spacing-4",
});

const CustomCaption = styled(Caption)({
  color: "!!$black",
  lineHeight: 0.9,
});

const Logo = styled("img")({
  width: "60px",
  borderRadius: "$radius-small",
  boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
});

const ViewButton = styled(Button)({
  marginLeft: "auto",
  minWidth: "unset",
  padding: "$spacing-10",
  minHeight: "unset",
  fontWeight: "100",
  fontSize: "16px",
});
